/**
 * Toast notification component — lightweight, no dependencies.
 * Usage:
 *   import { showToast } from './Toast';
 * showToast('Máquina pronta! ', 'success');
 */

import { useState, useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { T } from '../styles/tokens';

interface ToastItem {
  id: number;
  message: string;
  type: 'success' | 'info' | 'error';
  exiting?: boolean;
}

const toasts = signal<ToastItem[]>([]);
let nextId = 1;

export function showToast(message: string, type: 'success' | 'info' | 'error' = 'info', durationMs = 7000) {
  const id = nextId++;
  toasts.value = [...toasts.value, { id, message, type }];

  setTimeout(() => {
    // Mark as exiting for animation
    toasts.value = toasts.value.map(t => t.id === id ? { ...t, exiting: true } : t);
    setTimeout(() => {
      toasts.value = toasts.value.filter(t => t.id !== id);
    }, 350);
  }, durationMs);
}

const COLORS = {
 success: { bg: T.successBg, border: T.successBg, text: T.success, icon: '' },
 info: { bg: T.primaryBg, border: T.primaryBg, text: T.primary, icon: '' },
 error: { bg: T.dangerBg, border: T.dangerBg, text: T.danger, icon: '' },
};

export function ToastContainer() {
  const items = toasts.value;
  if (items.length === 0) return null;

  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 99999,
      display: 'flex', flexDirection: 'column', gap: 10,
      pointerEvents: 'none', maxWidth: 380,
    }}>
      {items.map(t => {
        const c = COLORS[t.type] || COLORS.info; // Fallback to info if type is invalid
        return (
          <div
            key={t.id}
            role="status"
            aria-live="polite"
            style={{
              padding: '14px 20px',
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: 14,
              color: 'var(--text, #E8EEF9)',
              fontSize: 14,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              backdropFilter: 'blur(12px)',
              boxShadow: `0 8px 32px ${T.blackAlpha30}`,
              animation: t.exiting ? 'toast-out 0.35s ease forwards' : 'toast-in 0.35s ease',
              pointerEvents: 'auto',
            }}
          >
            <span style={{ fontSize: 18, flexShrink: 0 }}>{c.icon}</span>
            <span>{t.message}</span>
          </div>
        );
      })}
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateX(40px) scale(0.95); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes toast-out {
          from { opacity: 1; transform: translateX(0) scale(1); }
          to   { opacity: 0; transform: translateX(40px) scale(0.95); }
        }
      `}</style>
    </div>
  );
}
